import React, { useState } from 'react';
import { isLinkComplete, type AdminTransferDetail, type LinkDeviceRequest, type MerchantLink } from '@platform/shared-types';
import { businessTransferApi } from '../../../services/businessTransferApi';
import { toastService } from '../../../services/toastService';
import { when } from '../format';

type P = { detail: AdminTransferDetail; canAct: boolean; onChange: (d: AdminTransferDetail) => void };

const FIELDS: Record<string, Array<[keyof NonNullable<LinkDeviceRequest['credentials']>, string]>> = {
  dejavoo_spin_usb: [['spinTpn', 'TPN (12 digits)'], ['spinRegisterId', 'SPIn Register Id'], ['spinAuthKey', 'SPIn Auth Key']],
  dejavoo_spin_cloud: [['spinTpn', 'TPN (12 digits)'], ['spinRegisterId', 'SPIn Register Id'], ['spinAuthKey', 'SPIn Auth Key']],
  // RubyTill Pay pairs with a code from Super Admin — the TPN is its only credential.
  dejavoo_rubytill_pay: [['spinTpn', 'TPN (12 digits)']],
  dejavoo_cloud: [['spinTpn', 'TPN (12 digits)'], ['iposApiKey', 'iPOS API key'], ['iposSecretKey', 'iPOS secret key'], ['iposAuthToken', 'iPOS auth token']],
  cloud: [['terminalIdNmi', 'NMI device id'], ['deviceLicense', 'Device licence (optional)']],
  nmi_direct_vp3350: [],
  online_gateway: [['securityKey', 'Gateway security key'], ['merchantId', 'Merchant id (optional)']],
};

function Chip({ ok, label }: { ok: boolean; label: string }) {
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'}`}>{label}</span>;
}

function LinkRow({ link, transferId, canAct, onChange }: { link: MerchantLink; transferId: string; canAct: boolean; onChange: P['onChange'] }) {
  const fields = FIELDS[link.kind === 'online_gateway' ? 'online_gateway' : link.deviceClass || ''] || [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const send = async (body: LinkDeviceRequest, ok: string) => {
    setBusy(true);
    try { onChange(await businessTransferApi.link(transferId, link.key, body)); toastService.success(ok); setValues({}); }
    catch (e: any) { toastService.error(e.message); }
    finally { setBusy(false); }
  };
  const done = isLinkComplete(link);
  return (
    <tr className="border-t border-stone-100 align-top">
      <td className="px-3 py-3"><b>{link.label}</b>{link.serialNumber && <div className="font-mono text-xs text-stone-500">SN {link.serialNumber}</div>}</td>
      <td className="px-3 py-3"><Chip ok={false} label={`Detached ${when(link.detachedAt)}`} /></td>
      <td className="px-3 py-3">
        {link.credentialsSavedAt
          ? <span className="font-mono text-xs">{link.summary}</span>
          : canAct && (
            <form className="flex flex-col gap-1.5" onSubmit={(e) => { e.preventDefault(); send({ credentials: values as any }, 'Credentials saved'); }}>
              {fields.map(([key, label]) => (
                <input key={key} placeholder={label} value={values[key] || ''} onChange={(e) => setValues({ ...values, [key]: e.target.value })}
                  className="min-h-[44px] rounded-lg border border-stone-300 px-2 font-mono text-xs" autoComplete="off" />
              ))}
              <button type="submit" disabled={busy} className="min-h-[44px] rounded-lg border border-stone-300 bg-white text-xs font-semibold">{fields.length ? 'Save credentials' : 'Confirm — uses gateway key'}</button>
            </form>
          )}
      </td>
      <td className="px-3 py-3">
        {link.parametersDownloadedAt ? <Chip ok label="Downloaded" /> : canAct && link.credentialsSavedAt && (
          <button type="button" disabled={busy} onClick={() => send({ parametersDownloaded: true }, 'Parameters marked downloaded')} className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-xs font-semibold">Parameters downloaded</button>
        )}
      </td>
      <td className="px-3 py-3">
        {link.testSaleAt ? <Chip ok label="$1.00 approved · refunded" /> : canAct && link.parametersDownloadedAt && (
          <button type="button" disabled={busy} onClick={() => send({ testSaleApproved: true }, 'Test sale recorded')} className="min-h-[44px] rounded-lg bg-[#DC143C] px-3 text-xs font-semibold text-white">Test sale approved</button>
        )}
      </td>
      <td className="px-3 py-3">{done ? <Chip ok label="Linked" /> : <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-800">Pending</span>}</td>
    </tr>
  );
}

export default function MerchantLinkPanel({ detail, canAct, onChange }: P) {
  const t = detail.transfer;
  if (t.run?.status !== 'done') return <div className="text-sm text-stone-600">Available after the transfer runs.</div>;
  const done = t.links.filter(isLinkComplete).length;
  return (
    <div className="flex flex-col gap-3 text-sm">
      {done < t.links.length && (
        <div className="rounded-lg border border-orange-300 bg-orange-50 px-3 py-2 text-orange-900">
          <b>Card payments are pending.</b> The seller's merchant account was detached {when(t.run.finishedAt)}. Cash and other tenders are working.
        </div>
      )}
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span><b>VAR sheet:</b> {t.varSheet ? `MID ${t.varSheet.mid} · ${t.varSheet.processor}` : 'not recorded yet'}</span>
        <span className="text-stone-600"><b className="text-stone-900">{done} of {t.links.length} linked.</b> The transfer can't complete until every device is linked. Come back any time.</span>
      </div>
      <div className="overflow-x-auto rounded-lg border border-stone-200">
        <table className="w-full">
          <thead className="bg-stone-50"><tr>{['Card device', 'Seller account', 'New account', 'Parameters', 'Test sale', 'Status'].map((h) => <th key={h} className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-stone-500">{h}</th>)}</tr></thead>
          <tbody>{t.links.map((l) => <LinkRow key={l.key} link={l} transferId={t.transferId} canAct={canAct} onChange={onChange} />)}</tbody>
        </table>
      </div>
    </div>
  );
}
